<template>
  <div class="min-w-[160px] flex flex-col gap-[10px]">
    <!-- preview -->
    <div
      class="min-h-[160px] relative border-[1px] border-solid border-[#FBF5FF]"
    >
      <img
        v-if="croppedImage"
        class="absolute left-0 top-0 w-full h-full object-cover"
        :src="croppedImage"
        alt="preview"
      />
      <img
        v-else-if="imageDefault == 'shelter'"
        class="absolute left-0 top-0 w-full h-full object-cover"
        src="../assets/images/shelter-default.png"
        alt="preview"
      />
      <img
        v-else-if="imageDefault == 'resource'"
        class="absolute left-0 top-0 w-full h-full object-cover"
        src="../assets/images/resource-default.png"
        alt="preview"
      />
      <img
        v-else
        class="absolute left-0 top-0 w-full h-full object-cover"
        src="../assets/images/user-default.png"
        alt="preview"
      />
    </div>

    <!-- hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="hidden"
      @change="handleFileChange"
    />

    <!-- buttons -->
    <div v-if="isRealImage" class="w-full flex items-center gap-[10px]">
      <ButtonComponent
        icon="TrashcanIcon"
        :bClass="'min-w-[42px] min-h-[42px] !border-[#B7B2B5] bg-[#fff] text-[#9b237b]'"
        :lClass="'!text-[13px]'"
        @clicked="removeImage"
      />
      <ButtonComponent
        label="แก้ไขรูป"
        :bClass="'w-full !max-h-[42px] py-[11px] px-[16px] !border-[#9B237B] bg-[#FBEFF8] text-[#9B237B]'"
        :lClass="'!text-[13px]'"
        @clicked="triggerUpload"
      />
    </div>
    <ButtonComponent
      v-else
      label="อัปโหลดรูป"
      :bClass="'w-full !max-h-[42px] py-[11px] px-[16px] !border-[#9B237B] bg-[#9B237B] text-[#fff]'"
      :lClass="'!text-[13px]'"
      @clicked="triggerUpload"
    />
  </div>

  <!-- cropper modal -->
  <div
    v-if="showCropper"
    class="fixed inset-0 bg-black/60 z-50 flex justify-center items-center"
  >
    <div
      class="w-[90%] h-[500px] flex flex-col justify-center gap-[22px] bg-white p-4 rounded-[6px] shadow-lg max-w-[550px]"
    >
      <Cropper
        ref="cropperRef"
        class="h-[396px] bg-black w-full"
        :src="imagePreview"
        :stencil-props="stencilProps"
      />
      <div class="flex items-center justify-center gap-[10px]">
        <ButtonComponent
          label="ยกเลิก"
          :bClass="'btn_shadow--cancel'"
          @clicked="showCropper = false"
        />
        <ButtonComponent
          label="ตกลง"
          :bClass="'btn_shadow--save'"
          @clicked="cropImage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

const props = defineProps({
  image: {
    type: [String, Number],
    default: "",
  },
  imageDefault: {
    type: String,
    default: "user",
  },
});
const emit = defineEmits(["update:image"]);

// refs
const cropperRef = ref(null);
const showCropper = ref(false);
const fileInput = ref(null);
const imagePreview = ref("");
const croppedImage = ref(null);
const rawImage = ref(null);

// stencil options
const stencilProps = ref({
  aspectRatio: 1,
  movable: true,
  scalable: true,
  resizable: true,
});

// sync external image
watch(
  () => props.image,
  (val) => {
    if (typeof val === "string") {
      croppedImage.value = val;
    }
  },
  { immediate: true }
);

const isRealImage = computed(() => {
  return (
    croppedImage.value &&
    ![
      "shelter_default.png",
      "user_default.png",
      "product_default",
      "shelter-default.png",
      "user-default.png",
      "product-default",
    ].some((name) => croppedImage.value?.includes(name))
  );
});

// trigger upload
const triggerUpload = () => {
  fileInput.value?.click();
};

// file input change
const handleFileChange = (e) => {
  const file = e.target.files?.[0];
  e.target.value = ""; // reset input value for next upload of same file

  if (
    file &&
    /\.(jpe?g|png)$/i.test(file.name) &&
    ["image/jpeg", "image/png"].includes(file.type)
  ) {
    rawImage.value = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      imagePreview.value = e.target.result;
      showCropper.value = true;
    };
    reader.readAsDataURL(file);
  }
};

// crop image
const cropImage = async () => {
  const result = cropperRef.value?.getResult?.();

  if (result?.canvas) {
    result.canvas.toBlob((blob) => {
      if (blob) {
        const croppedFile = new File([blob], rawImage.value.name, {
          type: blob.type,
        });
        croppedImage.value = URL.createObjectURL(croppedFile);
        emit("update:image", croppedFile);
        showCropper.value = false;
        // console.log("updated image", croppedFile);
      }
    }, "image/jpeg");
  } else {
    console.warn("ไม่สามารถตัดรูปได้");
  }
};

// remove image
const removeImage = () => {
  croppedImage.value = null;
  emit("update:image", null);
};
</script>
